// Generated by GitHub Copilot
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import styles from './WhyAccenture.module.css';
import gccPlatformImg from '../../assets/images/whyAccentureImages/architecture.jpg';
import aiRefineryImg from '../../assets/images/whyAccentureImages/platform.png';
import ansrLogo from '../../assets/images/whyAccentureImages/ansr.png';
import ecoImage from '../../assets/images/whyAccentureImages/ecoImage.png';
import trackImage from '../../assets/images/whyAccentureImages/trackImage.png';
import thumbnailImg from '../../assets/images/whyAccentureImages/WhyAccentureThumbnail.png';

const VIDEO_SRC = '/videos/accenture_whyaccentureforgccs_1080p_h264.mp4';

/**
 * Why Accenture page.
 * Hero section highlights Scale, AI and ANSR differentiators.
 * Uses the same dark/purple bokeh gradient theme as the Home page.
 */
const WhyAccenture = () => {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlay = () => {
    setIsPlaying(true);
    setTimeout(() => videoRef.current?.play(), 0);
  };

  return (
    <div className={styles.page}>
      <Navbar />
      <div className={styles.heroPadding} />

      {/* ── Back button bar ── */}
      <div className={styles.backBar}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={styles.backButton}
          aria-label="Go back to previous page"
        >
          <svg className={styles.backIcon} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>

      {/* ── Hero Section ── */}
      <section className={styles.heroSection} aria-labelledby="why-accenture-heading">

        {/* Bokeh background blobs */}
        <div aria-hidden="true" className={styles.bokehContainer}>
          <div className={styles.blobLeftLarge} />
          <div className={styles.blobMidLeft} />
          <div className={styles.blobCenterTop} />
          <div className={styles.blobCenter} />
          <div className={styles.blobRight} />
          <div className={styles.blobFarRight} />
        </div>

        {/* ── Split layout: left text | divider | right image ── */}
        <div className={styles.heroContent}>

          {/* Left — text panel */}
          <div className={styles.heroLeft}>
            <p className={styles.eyebrow} aria-label="Section: Why Accenture">WHY ACCENTURE</p>
            <h1 id="why-accenture-heading" className={styles.heroHeading}>
              Scale, AI and ANSR. No one else has all three.
            </h1>
            <p className={styles.heroSubtext}>
              Accenture combines operating DNA, platform-led AI capability and the ANSR alliance
              to design, set up, run and transform global capability centers.
            </p>
          </div>

          {/* Vertical divider */}
          <div className={styles.verticalDivider} aria-hidden="true" />

          {/* Right — video panel */}
          <div className={styles.heroRight}>
            <div className={styles.videoWrapper}>
              {isPlaying && (
                <video
                  ref={videoRef}
                  className={styles.videoEl}
                  controls
                  preload="metadata"
                  aria-label="Why Accenture for GCCs video"
                >
                  <source src={VIDEO_SRC} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
              {!isPlaying && (
                <>
                  <img
                    src={thumbnailImg}
                    alt="The only partner for the full GCC lifecycle"
                    className={styles.videoThumbnail}
                  />
                  <div className={styles.videoGradient} aria-hidden="true" />
                  <button
                    type="button"
                    className={styles.videoLink}
                    aria-label="Play Why Accenture for GCCs video"
                    onClick={handlePlay}
                  >
                    <div className={styles.playButton} aria-hidden="true">
                      <svg className={styles.playIcon} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <hr className={styles.sectionDivider} aria-hidden="true" />

 {/* ── Three Reasons Section ── */}
      <section className={styles.reasonsSection} aria-labelledby="reasons-heading">
        <div className={styles.reasonsContent}>

          {/* Top row: heading left, subtext right */}
          {/* <div className={styles.reasonsTopRow}>
            <div className={styles.reasonsLeft}>
              <p className={styles.reasonsEyebrow}>THREE REASONS TO CHOOSE ACCENTURE</p>
              <h2 id="reasons-heading" className={styles.reasonsHeading}>
                No other partner combines all three.
              </h2>
            </div>
            <div className={styles.reasonsRight}>
              <p className={styles.reasonsSubtext}>
                This points form a simple differentiator: operating DNA,
                AI/platform advantage and ecosystem alliance.
              </p>
            </div>
          </div> */}

          {/* 8-cell grid — header + 7 content cards */}
          <div className={styles.strengthGrid}>

            {/* Cell 1 — Header */}
            <div className={styles.reasonsCard}>
              <div className={styles.reasonsCardAccent} aria-hidden="true" />
              {/* <p className={styles.reasonsHeaderEyebrow}>Why Accenture</p> */}
              <h2 className={styles.reasonsHeaderTitle}>Our strength<br />in numbers</h2>
            </div>

            {/* Cell 2 — Strong financials */}
            <div className={styles.reasonsCard}>
              <div className={styles.reasonsCardAccent} aria-hidden="true" />
              <h3 className={styles.reasonsCardTitle}>Strong financials</h3>
              <div className={styles.reasonsStats}>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>$74.2B</span>
                  <span className={styles.reasonsStatDesc}>FY26 Accenture Revenues</span>
                </div>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>$2B+</span>
                  <span className={styles.reasonsStatDesc}>GCC Sales in FY26</span>
                </div>
              </div>
            </div>

            {/* Cell 3 — Client-focused */}
            <div className={styles.reasonsCard}>
              <div className={styles.reasonsCardAccent} aria-hidden="true" />
              <h3 className={styles.reasonsCardTitle}>Client-focused</h3>
              <div className={styles.reasonsStats}>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>260+</span>
                  <span className={styles.reasonsStatDesc}>GCCs built or transformed</span>
                </div>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>225+</span>
                  <span className={styles.reasonsStatDesc}>Additional GCCs setup by ANSR</span>
                </div>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>25+</span>
                  <span className={styles.reasonsStatDesc}>Build-Operate-Transform-Transform clients of Accenture</span>
                </div>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>130+</span>
                  <span className={styles.reasonsStatDesc}>GCCs reskilled for AI</span>
                </div>
              </div>
            </div>

            {/* Cell 4 — Strategic investments */}
            <div className={styles.reasonsCard}>
              <div className={styles.reasonsCardAccent} aria-hidden="true" />
              <h3 className={styles.reasonsCardTitle}>Strategic investments</h3>
              <div className={styles.reasonsStats}>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>$3B</span>
                  <span className={styles.reasonsStatDesc}>Multi-year investment in AI</span>
                </div>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>$6.9B</span>
                  <span className={styles.reasonsStatDesc}>Investments in V&A, R&D, and Learning & Development​</span>
                </div>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>$250M+</span>
                  <span className={styles.reasonsStatDesc}>GCC specific investments</span>
                </div>
              </div>
            </div>

            {/* Cell 5 — Global delivery */}
            <div className={styles.reasonsCard}>
              <div className={styles.reasonsCardAccent} aria-hidden="true" />
              <h3 className={styles.reasonsCardTitle}>Global delivery</h3>
              <div className={styles.reasonsStats}>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>25,000+</span>
                  <span className={styles.reasonsStatDesc}>Dedicated GCC practitioners</span>
                </div>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>20+</span>
                  <span className={styles.reasonsStatDesc}>GCC client industries served</span>
                </div>
                <div className={styles.strengthLabelStat}>
                  <span className={styles.reasonsCardTitle}>Global GCC Hubs</span>
                  <span className={styles.reasonsStatDesc}>Credential across the all leading GCC locations across the globe</span>
                </div>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>350,000+</span>
                  <span className={styles.reasonsStatDesc}>Global talent in India across Tech &amp; Ops</span>
                </div>
              </div>
            </div>

            {/* Cell 6 — Vast experience */}
            <div className={styles.reasonsCard}>
              <div className={styles.reasonsCardAccent} aria-hidden="true" />
              <h3 className={styles.reasonsCardTitle}>Vast experience</h3>
              <div className={styles.reasonsStats}>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>~20</span>
                  <span className={styles.reasonsStatDesc}>Years of GCC experience</span>
                </div>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>14M+</span>
                  <span className={styles.reasonsStatDesc}>Global real-estate Sq Ft</span>
                </div>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>350,000+</span>
                  <span className={styles.reasonsStatDesc}>FTE transition experience</span>
                </div>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>10+</span>
                  <span className={styles.reasonsStatDesc}>GCC industry luminaries</span>
                </div>
              </div>
            </div>

            {/* Cell 7 — Proprietary assets */}
            <div className={styles.reasonsCard}>
              <div className={styles.reasonsCardAccent} aria-hidden="true" />
              <h3 className={styles.reasonsCardTitle}>Proprietary assets</h3>
              <div className={styles.reasonsStats}>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>1000+</span>
                  <span className={styles.reasonsStatDesc}>Automation and AI use cases across GCC Platform</span>
                </div>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>700+</span>
                  <span className={styles.reasonsStatDesc}>Gen AI and Agentic use cases</span>
                </div>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>5+</span>
                  <span className={styles.reasonsStatDesc}>GCCs with End-to-End Agentic Workflows deployed at scale</span>
                </div>
                <div className={styles.strengthLabelStat}>
                  <span className={styles.reasonsCardTitle}>LearnVantage</span>
                  <span className={styles.reasonsStatDesc}>Proprietary GCC learning academy</span>
                </div>
              </div>
            </div>

            {/* Cell 8 — Capability powerhouse */}
            <div className={styles.reasonsCard}>
              <div className={styles.reasonsCardAccent} aria-hidden="true" />
              <h3 className={styles.reasonsCardTitle}>Capability powerhouse</h3>
              <div className={styles.reasonsStats}>
                <div className={styles.reasonsStat}>
                  <span className={styles.reasonsStatValue}>#1</span>
                  <span className={styles.reasonsStatDesc}>Partner for all of our top 10 technology ecosystem partners</span>
                </div>
                <div className={styles.strengthLabelStat}>
                  <span className={styles.reasonsCardTitle}>ANSR x Accenture</span>
                  <span className={styles.reasonsStatDesc}>Exclusive partnership with the leader in GCC setup</span>
                </div>
                <div className={styles.strengthLabelStat}>
                  <span className={styles.reasonsCardTitle}>Leader</span>
                  <span className={styles.reasonsStatDesc}>Positioning by Industry Analysts in GCC Setup and Transformation by ISG, Everest, HfS and Avasant</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <hr className={styles.sectionDivider} aria-hidden="true" />


          {/* ── Awards & Recognition Section ── */}
      <section className={styles.awardsSection} aria-labelledby="awards-heading">
        <div className={styles.awardsInner}>
          <div className={styles.awardsEyebrowRow}>
            <p className={styles.awardsEyebrow}>AWARDS &amp; RECOGNITION</p>
          </div>
          <div className={styles.awardsGrid}>

            {/* Card 1 — Everest */}
            <div className={`${styles.awardsCard} ${styles.awardsCardBlue}`}>
              <div className={styles.awardsCardFront} aria-hidden="false">
                <div className={styles.awardsCardPattern} aria-hidden="true">
                  <svg viewBox="0 0 400 280" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.awardsPatternSvg}>
                    <circle cx="160" cy="130" r="80" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
                    <path d="M0 60 Q120 40 200 120 T400 80" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
                    <path d="M200 0 Q240 80 300 100 T400 60" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
                    <line x1="200" y1="0" x2="200" y2="280" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                  </svg>
                </div>
                <p className={styles.awardsCardText}>Everest names Accenture a Leader in GCC Setup and GCC Transformation in India 2026</p>
              </div>
              <div className={styles.awardsCardBack} aria-hidden="true">
                <p className={styles.awardsBackText}>
                  Accenture was named a Leader in the Everest Group GCC Setup Capabilities in India and GCC
                  Transformation Capabilities in India - PEAK Matrix&reg; Assessments 2026. We are #1 in
                  Transformation and a Leader in Set Up, where we earned Star Performer for the best year-on-year
                  improvement in the market.
                </p>
                {/* <a
                  href="https://www.accenture.com/content/dam/accenture/final/accenture-com/document-3/Accenture-Everest-Group-Global-In-house-Center-GIC-Setup-Capabilities-in-India-Provider-PEAK-Matrix-Assessment-2024.pdf#zoom=40"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.awardsBackLink}
                >Click to know more</a> */}
              </div>
            </div>

            {/* Card 2 — ISG */}
            <div className={`${styles.awardsCard} ${styles.awardsCardOrange}`}>
              <div className={styles.awardsCardFront} aria-hidden="false">
                <div className={styles.awardsCardPattern} aria-hidden="true">
                  <svg viewBox="0 0 400 280" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.awardsPatternSvg}>
                    <circle cx="260" cy="130" r="100" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
                    <path d="M0 100 Q150 60 280 140 T400 100" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
                    <line x1="260" y1="0" x2="260" y2="280" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                    <path d="M100 0 Q180 80 200 160 T280 280" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
                  </svg>
                </div>
                <p className={styles.awardsCardText}>Accenture named a Leader in ISG&apos;s Provider Lens&reg; - GCC Services 2026</p>
              </div>
              <div className={styles.awardsCardBack} aria-hidden="true">
                <p className={styles.awardsBackText}>
                  ISG named Accenture a Leader in its Provider Lens&reg; - Global Capability Center Services 2026
                  in two areas: 1. Design and Setup and 2. Optimization and Enhancement. ISG noted that Accenture
                  enhances GCCs with AI-driven transformation, automation and global expertise.
                </p>
                {/* <a
                  href="https://www.accenture.com/content/dam/accenture/final/accenture-com/document-3/Accenture-Global-Capability-Center-GCC-Services-2025.pdf#zoom=40"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.awardsBackLink}
                >Click to know more</a> */}
              </div>
            </div>

            {/* Card 3 — Avasant */}
            <div className={`${styles.awardsCard} ${styles.awardsCardGreen}`}>
              <div className={styles.awardsCardFront} aria-hidden="false">
                <div className={styles.awardsCardPattern} aria-hidden="true">
                  <svg viewBox="0 0 400 280" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.awardsPatternSvg}>
                    <circle cx="200" cy="140" r="90" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
                    <path d="M0 80 Q100 50 200 120 T400 90" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
                    <path d="M50 0 Q150 100 250 80 T400 140" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
                    <line x1="200" y1="0" x2="200" y2="280" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                  </svg>
                </div>
                <p className={styles.awardsCardText}>Avasant names Accenture a Leader in GCC Services RadarView&trade; 2026</p>
              </div>
              <div className={styles.awardsCardBack} aria-hidden="true">
                <p className={styles.awardsBackText}>
                  Accenture was recognized as a Leader in Avasant&apos;s GCC Services RadarView&trade; 2026.
                  Avasant highlighted how Accenture leverages an AI-driven digital thread, proprietary GCC
                  platforms and deep industry expertise to reinvent enterprise operations and innovation.
                </p>
              </div>
            </div>

            {/* Card 4 — HFS */}
            <div className={`${styles.awardsCard} ${styles.awardsCardPurple}`}>
              <div className={styles.awardsCardFront} aria-hidden="false">
                <div className={styles.awardsCardPattern} aria-hidden="true">
                  <svg viewBox="0 0 400 280" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.awardsPatternSvg}>
                    <circle cx="300" cy="100" r="110" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
                    <path d="M0 120 Q120 80 240 150 T400 110" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
                    <path d="M80 0 Q200 60 280 140 T400 200" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
                    <line x1="300" y1="0" x2="300" y2="280" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                  </svg>
                </div>
                <p className={styles.awardsCardText}>HFS places Accenture in Horizon 3 - GCC Orchestrators in HFS Horizons 2026</p>
              </div>
              <div className={styles.awardsCardBack} aria-hidden="true">
                <p className={styles.awardsBackText}>
                  HFS placed Accenture in Horizon 3 (GCC Orchestrators) in its HFS Horizons: GCC Services, 2026
                  report, noting that Accenture combines flexible GCC ownership models, scalable mid-market
                  constructs and AI-infused automation to deliver sustained double-digit annual productivity gains.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>
   {/* <hr className={styles.sectionDivider} aria-hidden="true" /> */}
      {/* ── Let There Be Change ── */}
      {/* Generated by GitHub Copilot */}
      {/* <section className={styles.ltbcSection} aria-labelledby="ltbc-heading">
        <div className={styles.ltbcInner}>
          <div className={styles.ltbcColumns}> */}

            {/* Left — label + heading */}
            {/* <div className={styles.ltbcLeft}>
              <div className={styles.ltbcLabelRow}>
                <p className={styles.ltbcLabel}>LET THERE BE CHANGE</p>
              </div>
              <h2 id="ltbc-heading" className={styles.ltbcHeading}>
                Build the GCC that makes reinvention repeatable.
              </h2>
            </div> */}

            {/* Right — subtext + pills */}
            {/* <div className={styles.ltbcRight}>
              <p className={styles.ltbcSubtext}>
                Use Accenture when the problem requires more than setup: a partner with scale, AI
                platforms, ecosystem{' '}
                <span className={styles.ltbcUnderline}>access</span> and the operating DNA to keep
                transforming after launch.
              </p>

              <div className={styles.ltbcPills} role="list" aria-label="Key differentiators">
                <div className={styles.ltbcPill} role="listitem">Scale</div>
                <div className={styles.ltbcArrow} aria-hidden="true">
                  <svg width="40" height="16" viewBox="0 0 40 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 8H36M36 8L29 2M36 8L29 14" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className={styles.ltbcPill} role="listitem">AI platforms</div>
                <div className={styles.ltbcArrow} aria-hidden="true">
                  <svg width="40" height="16" viewBox="0 0 40 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 8H36M36 8L29 2M36 8L29 14" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className={styles.ltbcPill} role="listitem">Ecosystem access</div>
              </div>
            </div> */}

          {/* </div>
        </div>
      </section> */}

      <Footer />
    </div>
  );
};

export default WhyAccenture;
